import { type BenefitItem } from '@karehero/llama/pwa/pages/EligibilityResults/EligibilityResults';

type BenefitData = Omit<BenefitItem, 'icon'> & { icon: string };

export const benefitsMap: Record<string, BenefitData> = {
  'attendance-allowance': {
    benefitName: 'Attendance Allowance',
    benefitDescription:
      'A tax-free benefit for someone at State Pension age or older who needs help with personal care or supervision because of a disability or health condition. It does not cover mobility needs.',
    icon: 'hand-holding-heart',
    additionalBenefits: [
      'Weekly cash payments',
      'Two payment rates',
      'Tax free income',
      'Unlocks other benefits',
    ],
    annualValueGBP: {
      min: 3988.4,
      max: 5959.2,
      notes: 'Based on £76.70–£114.60 per week (2026/27 rates).',
    },
    savingsMessage: 'Up to £5,959 per year',
    eligibilityInformation: [
      'You are State Pension age or older',
      'You have a physical or mental disability or health condition',
      'You need help with personal care or supervision (you do not need to have a carer)',
      'You have needed help for at least 6 months (unless terminally ill)',
    ],
    applicationInstructions:
      "Apply using our Attendance Allowance form (we'll guide you through the claim step by step).",
    links: {
      info: '/attendance-allowance',
      apply: '/attendance-allowance/start',
    },
    sources: [
      'https://www.gov.uk/attendance-allowance',
      'https://www.gov.uk/government/publications/benefit-and-pension-rates-2026-to-2027',
    ],
  },
  'universal-credit': {
    benefitName: 'Universal Credit',
    benefitDescription:
      "A monthly payment to help someone with living costs when they're on a low income or out of work. The amount can include extra support for things like housing costs, children, caring responsibilities, or a health condition.",
    icon: 'money-bills',
    additionalBenefits: [
      'Monthly income support',
      'Housing cost help',
      'Disability extra payments',
      'Caring support',
    ],
    annualValueGBP: {
      min: 4062.96,
      max: 8003.64,
      notes:
        'Standard allowance only (no extras), using 2026/27 monthly rates. Single under 25: £338.58/mo; single 25+: £424.90/mo; couple both under 25: £528.34/mo; couple where one/both 25+: £666.97/mo. Actual awards can be higher with additional elements.',
    },
    savingsMessage: 'Up to £8,004 per year (standard allowance only)',
    eligibilityInformation: [
      'You live in the UK',
      "You're on a low income or out of work",
      'You have £16,000 or less in money, savings and investments (typical rule; some exceptions apply)',
      'You meet immigration and residency rules for benefits',
    ],
    applicationInstructions:
      "Apply online via GOV.UK Universal Credit. You'll normally need to create an account and provide details about housing, income, savings and household circumstances.",
    links: {
      info: 'https://www.gov.uk/universal-credit',
      apply: 'https://www.gov.uk/universal-credit/how-to-claim',
    },
    sources: [
      'https://www.gov.uk/universal-credit',
      'https://www.gov.uk/universal-credit/what-youll-get',
    ],
  },
  'pension-credit': {
    benefitName: 'Pension Credit',
    benefitDescription:
      "A means-tested benefit that can top up someone's weekly income if they're over State Pension age and on a low income. Some people may also qualify for Savings Credit depending on when they reached State Pension age.",
    icon: 'sterling-sign',
    additionalBenefits: [
      'Guaranteed income top up',
      'Boosts other entitlements',
      'Weekly payments',
    ],
    annualValueGBP: {
      min: 0,
      max: 18889,
      notes:
        'Top-up varies based on your current income. Maximum top-up to the published minimum weekly income levels is £238/week (single) or £363.25/week (couple), which is up to £12,376/year (single) or £18,889/year (couple), before any extra amounts.',
    },
    savingsMessage:
      'Up to £18,889 per year (if eligible and no other income, couple)',
    eligibilityInformation: [
      "You're over State Pension age",
      'You live in Great Britain (separate rules in Northern Ireland)',
      'Your income is below the relevant threshold (savings and certain income can affect entitlement)',
    ],
    applicationInstructions:
      'Apply via GOV.UK (online, phone, or by post). Claims can typically be backdated (see GOV.UK for the current backdating rules).',
    links: {
      info: 'https://www.gov.uk/pension-credit',
      apply: 'https://www.gov.uk/pension-credit/how-to-claim',
    },
    sources: [
      'https://www.gov.uk/pension-credit',
      'https://www.gov.uk/pension-credit/what-youll-get',
    ],
  },
  'personal-independence-payment': {
    benefitName: 'Personal Independence Payment (PIP)',
    benefitDescription:
      "A tax-free benefit for someone aged 16 to State Pension age whose long-term health condition or disability affects their daily living and/or mobility. It's not means-tested and can be paid alongside work.",
    icon: 'wheelchair',
    additionalBenefits: [
      'Daily living payments',
      'Mobility payments',
      'Tax free income',
      'Two payment levels',
    ],
    annualValueGBP: {
      min: 0,
      max: 10119.2,
      notes:
        'Maximum assumes enhanced Daily Living (£114.60/wk) + enhanced Mobility (£80.00/wk) = £194.60/week, ~£10,119.20/year (2026/27 rates). You can receive one component only, and rates depend on assessment.',
    },
    savingsMessage: 'Up to £10,119 per year',
    eligibilityInformation: [
      'Aged 16+ and under State Pension age',
      'Your condition or disability is expected to last at least 12 months in total (rules include past and future impact)',
      'Difficulties with daily living and/or mobility are assessed using a points-based process',
    ],
    applicationInstructions:
      "Start a claim via GOV.UK (typically by phone or post to begin, then completing 'How your disability affects you' form; assessment may follow).",
    links: {
      info: 'https://www.gov.uk/pip',
      apply: 'https://www.gov.uk/pip/how-to-claim',
    },
    sources: [
      'https://www.gov.uk/pip',
      'https://www.gov.uk/pip/how-much-youll-get',
      'https://www.gov.uk/government/publications/benefit-and-pension-rates-2026-to-2027',
    ],
  },
  'adult-disability-payment': {
    benefitName: 'Adult Disability Payment (Scotland)',
    benefitDescription:
      "A tax-free disability benefit in Scotland for someone whose long-term health condition or disability affects daily living and/or mobility. It's not means-tested and can include one or both components.",
    icon: 'wheelchair',
    additionalBenefits: [
      'Daily living support',
      'Mobility support',
      'Tax free income',
      'Single application',
    ],
    annualValueGBP: {
      min: 0,
      max: 10119.2,
      notes:
        'Maximum assumes enhanced Daily Living (£114.60/wk) + enhanced Mobility (£80.00/wk) = £194.60/week, ~£10,119.20/year (2026/27 rates). You can receive one component only, and rates depend on assessment.',
    },
    savingsMessage: 'Up to £10,119 per year',
    eligibilityInformation: [
      'You live in Scotland',
      'You have a disability or long-term health condition affecting daily living and/or mobility',
      'You meet age and residence rules set by Social Security Scotland',
    ],
    applicationInstructions:
      'Apply through Social Security Scotland (online or alternative application routes available).',
    links: {
      info: 'https://www.mygov.scot/adult-disability-payment',
      apply: 'https://www.mygov.scot/adult-disability-payment/how-to-apply',
    },
    sources: ['https://www.mygov.scot/adult-disability-payment'],
  },
  'council-tax-reduction': {
    benefitName: 'Council Tax Reduction (Council Tax Support)',
    benefitDescription:
      "A local scheme that can reduce someone's Council Tax bill if they're on a low income or receive certain benefits. Rules and discounts vary by council and by UK nation.",
    icon: 'house',
    additionalBenefits: [
      'Reduced council tax',
      'Up to full discount',
      'Means tested',
    ],
    annualValueGBP: {
      notes:
        'Not a fixed cash payment. Savings are up to 100% of your Council Tax bill (varies by local council and your band/liability).',
    },
    savingsMessage: 'Up to 100% off your Council Tax bill',
    eligibilityInformation: [
      'You are liable for Council Tax (owner or tenant)',
      "You're on a low income and/or claiming certain benefits",
      'Your savings/capital and household circumstances may affect entitlement (rules vary by council)',
    ],
    applicationInstructions:
      "Apply to your local council via GOV.UK postcode lookup (or your council's website).",
    links: {
      info: 'https://www.gov.uk/apply-council-tax-reduction',
      apply: 'https://www.gov.uk/apply-council-tax-reduction',
    },
    sources: ['https://www.gov.uk/apply-council-tax-reduction'],
  },
  'nhs-funded-nursing-care': {
    benefitName: 'NHS-funded Nursing Care (FNC)',
    benefitDescription:
      "A weekly NHS contribution towards the nursing care part of someone's fees in a nursing home, if they're assessed as needing care from a registered nurse. It does not cover accommodation costs.",
    icon: 'user-nurse',
    additionalBenefits: [
      'Weekly NHS payments',
      'Paid to care home',
      'Two rate system',
    ],
    annualValueGBP: {
      min: 13919.36,
      max: 19148.48,
      notes:
        'Based on NHS England rates from 1 April 2026: standard £267.68/week (~£13,919.36/year) and higher £368.24/week (~£19,148.48/year), where applicable.',
    },
    savingsMessage:
      'Up to £19,148 per year towards nursing care (where eligible)',
    eligibilityInformation: [
      'You live in England or Northern Ireland and are in (or moving into) a care home with nursing',
      "You've been assessed as needing nursing care from a registered nurse",
      "You're not eligible for full NHS Continuing Healthcare (if you were, NHS would cover more/all of care costs)",
    ],
    applicationInstructions:
      'Request an assessment via the NHS (often arranged through the care home, GP, social worker, or local NHS team).',
    links: {
      info: 'https://www.nhs.uk/social-care-and-support/money-work-and-benefits/nhs-funded-nursing-care/',
      apply:
        'https://www.nhs.uk/social-care-and-support/money-work-and-benefits/nhs-continuing-healthcare/',
    },
    sources: [
      'https://www.nhs.uk/social-care-and-support/money-work-and-benefits/nhs-funded-nursing-care/',
      'https://www.gov.uk/government/news/better-community-care-thanks-to-nursing-funding-boost',
    ],
  },
  'free-personal-care': {
    benefitName: 'Free Personal Care (Scotland)',
    benefitDescription:
      'In Scotland, someone with assessed care needs may be able to receive personal care services without being charged for the personal care element. Support is arranged through the local council following an assessment.',
    icon: 'hands-holding-heart',
    additionalBenefits: [
      'Personal hygiene support',
      'Daily living support',
      'Meal assistance',
      'Mobility support',
    ],
    annualValueGBP: {
      notes:
        'Not a fixed cash payment. Financial value depends on the assessed care package and local authority service costs.',
    },
    savingsMessage:
      'Free personal care services (value depends on your care plan)',
    eligibilityInformation: [
      'You live in Scotland',
      "You have assessed personal care needs (via your local council's social work/social care assessment)",
      'You meet the criteria for personal care as defined in Scottish guidance',
    ],
    applicationInstructions:
      'Contact your local council/social work department to request a community care (needs) assessment.',
    links: {
      info: 'https://www.mygov.scot/personal-nursing-care',
      apply: 'https://www.mygov.scot/applying-social-care-support',
    },
    sources: ['https://www.mygov.scot/personal-nursing-care'],
  },
  'free-nursing-care': {
    benefitName: 'Free Nursing Care (Scotland)',
    benefitDescription:
      'In Scotland, someone living in a care home with assessed nursing care needs may receive free nursing care. This is separate from free personal care and covers the cost of nursing care provided by a registered nurse.',
    icon: 'hands-holding-heart',
    additionalBenefits: [
      'Nursing care provided by registered nurses',
      'Covers nursing element of care home fees',
    ],
    annualValueGBP: {
      notes:
        'Not a fixed cash payment. The nursing care rate is set by the Scottish Government (currently £114.55/week).',
    },
    savingsMessage:
      'Free nursing care services (currently £114.55/week towards nursing care costs)',
    eligibilityInformation: [
      'You live in Scotland',
      'You have assessed nursing care needs',
      'You are living in a care home that provides nursing care',
    ],
    applicationInstructions:
      'Contact your local council/social work department to request a community care (needs) assessment.',
    links: {
      info: 'https://www.mygov.scot/personal-nursing-care',
      apply: 'https://www.mygov.scot/applying-social-care-support',
    },
    sources: ['https://www.mygov.scot/personal-nursing-care'],
  },
  'disabled-facilities-grant': {
    benefitName: 'Disabled Facilities Grant (DFG)',
    benefitDescription:
      'A grant that can help pay for essential home adaptations for a disabled person, such as ramps, stairlifts or an accessible bathroom. The rules and maximum grant amounts depend on where the property is in the UK.',
    icon: 'house-heart',
    additionalBenefits: [
      'Home adaptations funding',
      'Bathroom accessibility',
      'Mobility improvements',
    ],
    annualValueGBP: {
      min: 0,
      max: 36000,
      notes:
        'Maximum single-application amounts on GOV.UK: England up to £30,000; Wales up to £36,000; Northern Ireland up to £35,000. Scotland: DFG not available (different support routes).',
    },
    savingsMessage: 'Up to £36,000 for home adaptations',
    eligibilityInformation: [
      "You're disabled (or live with a disabled person) and the adaptations are necessary and appropriate",
      'The work is considered reasonable and practicable for the property',
      'You apply via your local council (or relevant local authority body in Northern Ireland/Wales)',
    ],
    applicationInstructions:
      'Apply through your local council (usually after an occupational therapist/home assessment).',
    links: {
      info: 'https://www.gov.uk/disabled-facilities-grants',
      apply: 'https://www.gov.uk/disabled-facilities-grants/how-to-claim',
    },
    sources: [
      'https://www.gov.uk/disabled-facilities-grants/what-youll-get',
      'https://www.gov.uk/disabled-facilities-grants',
    ],
  },
  'local-authority-social-care': {
    benefitName: 'Local Authority Social Care',
    benefitDescription:
      "Support arranged by local councils for an adult who needs help with daily living, such as home care, equipment, supported living, respite, or residential care. What's provided and what someone pays depends on needs and a financial assessment.",
    icon: 'building',
    additionalBenefits: [
      'Needs assessment',
      'Home care services',
      'Residential support',
      'Personal care budget',
    ],
    annualValueGBP: {
      notes:
        'Not a fixed cash benefit. The council may pay some or all costs depending on assessed needs and your financial assessment. Rules and financial thresholds vary across the UK and can change.',
    },
    savingsMessage:
      'Council-funded care support (value depends on your assessment)',
    eligibilityInformation: [
      'You have care and support needs (assessed by your local council)',
      'Your needs meet the local/national eligibility threshold (varies across the UK)',
      'A financial assessment may determine how much you contribute (means test)',
    ],
    applicationInstructions:
      'Request a needs assessment from your local council or social services department.',
    links: {
      info: 'https://www.gov.uk/social-care-and-support-guide',
      apply: 'https://www.gov.uk/apply-needs-assessment-social-services',
    },
    sources: [
      'https://www.gov.uk/apply-needs-assessment-social-services',
      'https://www.nhs.uk/social-care-and-support/help-from-social-services-and-charities/financial-assessment-means-test/',
    ],
  },
  'concessionary-bus-pass-england': {
    benefitName: 'Concessionary Bus Pass (England)',
    benefitDescription:
      'A pass that gives free off-peak bus travel on local services in England for someone who meets the age rules or qualifying disability criteria under the national concessionary travel scheme.',
    icon: 'bus',
    additionalBenefits: [
      'Free off peak travel',
      'Nationwide coverage',
      'Local routes',
    ],
    annualValueGBP: {
      notes:
        'Not a cash payment. Savings depend on travel frequency and fares.',
    },
    savingsMessage:
      'Free bus travel (potentially hundreds per year depending on use)',
    eligibilityInformation: [
      'You live in England',
      "You're eligible due to age (State Pension age) OR you're an 'eligible disabled' person under the ENCTS rules",
      'You apply through your local council/issuing authority',
    ],
    applicationInstructions:
      'Apply through your local council using the GOV.UK postcode finder (separate pages for older and disabled passes).',
    links: {
      info: 'https://www.gov.uk/apply-for-elderly-person-bus-pass',
      apply: 'https://www.gov.uk/apply-for-elderly-person-bus-pass',
    },
    sources: [
      'https://www.gov.uk/apply-for-elderly-person-bus-pass',
      'https://www.gov.uk/apply-for-disabled-bus-pass',
    ],
  },
  'concessionary-bus-pass-wales': {
    benefitName: 'Concessionary Bus Pass (Wales)',
    benefitDescription:
      'A travel card that gives free bus travel on eligible services in Wales for someone aged 60+ or with a qualifying disability. Some people may also qualify for a companion card.',
    icon: 'bus',
    additionalBenefits: [
      'Free bus travel',
      'Companion travel',
      'All Wales routes',
    ],
    annualValueGBP: {
      notes:
        'Not a cash payment. Savings depend on travel frequency and fares.',
    },
    savingsMessage:
      'Free bus travel (potentially hundreds per year depending on use)',
    eligibilityInformation: [
      'Your primary residence is in Wales',
      'You are aged 60+ OR you are an eligible disabled person (as defined by the Welsh scheme)',
    ],
    applicationInstructions:
      "Apply via Transport for Wales' Concessionary Travel Card portal (online application; paper routes also available).",
    links: {
      info: 'https://www.gov.wales/apply-bus-pass',
      apply: 'https://portal.tfw.wales/en/card-applications/60-and-over',
    },
    sources: [
      'https://www.gov.wales/apply-bus-pass',
      'https://portal.tfw.wales/',
    ],
  },
  'concessionary-bus-pass-scotland': {
    benefitName:
      'Concessionary Bus Pass (Scotland) – National Entitlement Card (NEC)',
    benefitDescription:
      'A National Entitlement Card (NEC) that gives free bus travel across Scotland for someone aged 60+ or with a qualifying disability, on eligible local and long-distance scheduled services.',
    icon: 'bus',
    additionalBenefits: [
      'Free bus travel',
      'Nationwide coverage',
      'Long distance routes',
    ],
    annualValueGBP: {
      notes:
        'Not a cash payment. Savings depend on travel frequency and fares.',
    },
    savingsMessage:
      'Free bus travel (potentially hundreds per year depending on use)',
    eligibilityInformation: [
      'You live in Scotland',
      'You are aged 60+ OR meet the disabled eligibility criteria for concessionary travel',
      'You apply for an NEC via the national portal (or local routes where provided)',
    ],
    applicationInstructions:
      'Apply/renew online via getyournec.scot (Transport Scotland provides official application guidance).',
    links: {
      info: 'https://www.transport.gov.scot/concessionary-travel/60plus-or-disabled-free-bus-travel/',
      apply: 'https://getyournec.scot/nec/',
    },
    sources: [
      'https://www.transport.gov.scot/concessionary-travel/60plus-or-disabled-free-bus-travel/',
      'https://www.transport.gov.scot/concessionary-travel/how-to-apply-for-or-renew-your-national-entitlement-card/',
      'https://getyournec.scot/nec/',
    ],
  },
  'concessionary-bus-pass-northern-ireland': {
    benefitName: 'Concessionary Bus Pass (Northern Ireland) – SmartPass',
    benefitDescription:
      'A SmartPass that gives free or discounted public transport travel in Northern Ireland for someone who meets the age rules or qualifying disability criteria. Different SmartPass types apply at different ages.',
    icon: 'bus',
    additionalBenefits: [
      'Free local travel',
      'Senior travel',
      'Cross border routes',
    ],
    annualValueGBP: {
      notes:
        'Not a cash payment. Savings depend on travel frequency and fares.',
    },
    savingsMessage:
      'Free or discounted travel (potentially hundreds per year depending on use)',
    eligibilityInformation: [
      'You are permanently resident in Northern Ireland (nidirect notes a minimum of 3 months residence for some passes)',
      'You meet age criteria (60–64 for 60+ SmartPass; 65+ for Senior SmartPass) or disability criteria (other SmartPass variants exist)',
    ],
    applicationInstructions:
      'Apply via nidirect guidance for the relevant SmartPass type.',
    links: {
      info: 'https://www.nidirect.gov.uk/articles/free-and-concessionary-bus-and-rail-travel',
      apply:
        'https://www.nidirect.gov.uk/articles/60-smartpass-and-senior-65-smartpass',
    },
    sources: [
      'https://www.nidirect.gov.uk/articles/free-and-concessionary-bus-and-rail-travel',
      'https://www.nidirect.gov.uk/articles/60-smartpass-and-senior-65-smartpass',
    ],
  },
};
